import { Controller } from "@hotwired/stimulus";

// controller to resize input fields in the feed, for example comment input and post input

export default class extends Controller {
  static targets = ["feedInput", "feedSubmit", "feedInputWrap", "cancelButton"];
  static values = {
    scrollTarget: String,
    autofocus: { type: Boolean, default: false },
  };

  hasAttachments = false;
  isUploading = false;

  get isTextarea() {
    return this.hasFeedInputTarget;
  }

  get commentEditor() {
    return this.application.getControllerForElementAndIdentifier(
      this.element,
      "comment-editor",
    );
  }

  connect() {
    if (this.isTextarea) {
      this.resize();

      if (this.autofocusValue) {
        // Double-rAF defers focus until layout is fully painted after Turbo
        // frame replacement, so sticky positioning calculates correctly above
        // the keyboard on iOS Safari.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.feedInputTarget.focus();
            this.feedInputTarget.setSelectionRange(
              this.feedInputTarget.value.length,
              this.feedInputTarget.value.length,
            );
            this.feedInputTarget.scrollIntoView({ block: "nearest" });
          });
        });
      }
    }
    // TipTap editor mode: autofocus and initial state handled by comment_editor_controller
  }

  disconnect() {
    if (this.boundViewportResize) {
      window.visualViewport?.removeEventListener(
        "resize",
        this.boundViewportResize,
      );
    }
  }

  cancelReply() {
    const container = this.element.querySelector('[id^="reply_context_"]');
    container?.replaceChildren();
  }

  // On iOS PWA, tapping the submit button while the keyboard is open fires
  // pointerdown → blur (keyboard closes + layout shifts) → click lands nowhere.
  // Preventing the default on pointerdown stops the blur, keeping the layout
  // stable so the form can be submitted immediately via requestSubmit().
  submitOnPointerDown(event) {
    event.preventDefault();
    this.element.requestSubmit();
  }

  reset(event) {
    // Only reset on successful submission
    if (event.detail.success) {
      this.hasAttachments = false;
      this.isUploading = false;
      this.element.reset();

      if (this.isTextarea) {
        this.resize();
      } else {
        this.commentEditor?.clear();
        this.updateEditorState(false);
      }

      // Use turbo:before-stream-render and wrap the render function
      document.addEventListener(
        "turbo:before-stream-render",
        this.wrapRenderWithScroll,
        { once: true },
      );
    }
  }

  // Called via comment-editor:change event from comment_editor_controller
  editorChanged() {
    const hasContent = !this.commentEditor?.isEmpty();
    this.updateEditorState(hasContent);
  }

  // Called via comment-attachment:change event
  attachmentChanged(event) {
    this.hasAttachments = event.detail?.hasFiles || false;
    this.#updateSubmitVisibility();
  }

  // Called via comment-attachment:uploading event
  uploadStarted() {
    this.isUploading = true;
    if (this.hasFeedSubmitTarget) {
      this.feedSubmitTarget.disabled = true;
    }
  }

  // Called via comment-attachment:upload-complete event
  uploadComplete() {
    this.isUploading = false;
    if (this.hasFeedSubmitTarget) {
      this.feedSubmitTarget.disabled = false;
    }
  }

  updateEditorState(hasContent) {
    const shouldShow = hasContent || this.hasAttachments;

    if (shouldShow) {
      this.feedInputWrapTarget.classList.add("filled");
      if (this.hasFeedSubmitTarget) {
        this.feedSubmitTarget.classList.remove("button--hidden");
      }
    } else {
      this.feedInputWrapTarget.classList.remove("filled");
      if (this.hasFeedSubmitTarget) {
        this.feedSubmitTarget.classList.add("button--hidden");
      }
    }
  }

  #updateSubmitVisibility() {
    if (this.isTextarea) {
      const hasContent =
        this.hasFeedInputTarget && this.feedInputTarget.value.trim().length > 0;
      const shouldShow = hasContent || this.hasAttachments;
      if (shouldShow) {
        this.feedInputWrapTarget.classList.add("filled");
        if (this.hasFeedSubmitTarget) {
          this.feedSubmitTarget.classList.remove("button--hidden");
        }
      } else {
        this.feedInputWrapTarget.classList.remove("filled");
        if (this.hasFeedSubmitTarget) {
          this.feedSubmitTarget.classList.add("button--hidden");
        }
      }
    } else {
      const hasContent = !this.commentEditor?.isEmpty();
      this.updateEditorState(hasContent);
    }
  }

  wrapRenderWithScroll = (event) => {
    const originalRender = event.detail.render;
    const streamElement = event.target;
    const targetId = streamElement.getAttribute("target");

    event.detail.render = async (streamElement) => {
      await originalRender(streamElement);

      // Now the element is in the DOM, scroll to it
      const container = document.getElementById(targetId);
      if (container?.lastElementChild) {
        container.lastElementChild.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    };
  };

  #setupKeyboardDetection() {
    if (!window.visualViewport) return;

    const inputLine = this.element.closest(".comments__input-line");
    if (!inputLine) return;

    const initialHeight = window.visualViewport.height;

    this.boundViewportResize = () => {
      const keyboardOpen = window.visualViewport.height < initialHeight * 0.8;
      inputLine.classList.toggle("keyboard-open", keyboardOpen);
    };

    window.visualViewport.addEventListener("resize", this.boundViewportResize);
  }

  resize() {
    if (!this.isTextarea) return;

    const maxHeight = parseFloat(
      getComputedStyle(this.feedInputTarget).maxHeight,
    );

    // Reset to min-height first, so scrollHeight reflects actual content size
    this.feedInputTarget.style.height = "24px";
    const newHeight = Math.min(this.feedInputTarget.scrollHeight, maxHeight);
    this.feedInputTarget.style.height = `${newHeight}px`;

    // Checks if the field is empty after removing leading/trailing whitespace.
    // This prevents submitting comments that only contain spaces.
    const hasContent = this.feedInputTarget.value.trim().length > 0;

    if (hasContent) {
      this.feedInputWrapTarget.classList.add("filled");
      if (this.hasFeedSubmitTarget) {
        this.feedSubmitTarget.classList.remove("button--hidden");
      }
      if (this.hasCancelButtonTarget) {
        this.cancelButtonTarget.style.display = "block";
      }
    } else {
      this.feedInputWrapTarget.classList.remove("filled");
      if (this.hasFeedSubmitTarget) {
        this.feedSubmitTarget.classList.add("button--hidden");
      }
      if (this.hasCancelButtonTarget) {
        this.cancelButtonTarget.style.display = "none";
      }
      this.feedInputTarget.style.height = "";

      // If the field was only whitespace, clear it to reset the placeholder.
      if (this.feedInputTarget.value.length > 0) {
        this.feedInputTarget.value = "";
      }
    }
  }
}
